import { ApiSection, ConfigValue, EnvironmentAdminConfig, EnvironmentFormState } from './adminConfig.types';
import { applicationFields, clone, fieldDefault, flatFields } from './configRules';

/** The channel response as the panel reads it (`GET /channel/<id>` → `data`). */
export interface EnvironmentChannelResponse {
  basic_info?: Record<string, ConfigValue>;
  commend?: Record<string, ConfigValue>;
  guide?: Record<string, ConfigValue>;
  applications?: Record<string, Record<string, ConfigValue>>;
}

/** The save payload: every flat setting in its section, plus every application's values. */
export interface EnvironmentSavePayload {
  basic_info: Record<string, ConfigValue>;
  commend: Record<string, ConfigValue>;
  guide: Record<string, ConfigValue>;
  applications: Record<string, Record<string, ConfigValue>>;
}

const isPlainObject = (value: unknown): value is Record<string, ConfigValue> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Build the panel's working values from the channel response. Every key the config defines is
 * present: the stored value when there is one, else the field's default (older environment
 * documents predate most keys). Values are used exactly as stored — "Yes"/"No" stay strings.
 */
export const fromApi = (response: EnvironmentChannelResponse, config: EnvironmentAdminConfig): EnvironmentFormState => {
  const flat: Record<string, ConfigValue> = {};
  for (const { field, tab } of flatFields(config)) {
    const stored = (response[tab.apiSection as ApiSection] ?? {})[field.dbKey as string];
    flat[field.dbKey as string] = stored ?? clone(field.default);
  }
  const applications: Record<string, Record<string, ConfigValue>> = {};
  for (const tab of config.tabs.filter((t) => !t.apiSection)) {
    const stored = response.applications?.[tab.id] ?? {};
    applications[tab.id] = Object.fromEntries(
      applicationFields(tab).map((field) => {
        const value = stored[field.key];
        if (value === undefined) return [field.key, fieldDefault(field)];
        if (field.type === 'group' && isPlainObject(value)) {
          return [field.key, { ...(fieldDefault(field) as Record<string, ConfigValue>), ...value }];
        }
        return [field.key, clone(value)];
      }),
    );
  }
  return { flat, applications };
};

/**
 * Build the save payload. It always carries every setting the config defines (not only the
 * changed ones), so saving an older environment fills in the keys its document lacks.
 * `channel_key` is echoed back in basic_info exactly as before; the backend ignores it.
 */
export const toPayload = (
  state: EnvironmentFormState,
  config: EnvironmentAdminConfig,
  channelKey: string,
): EnvironmentSavePayload => {
  const payload: EnvironmentSavePayload = { basic_info: {}, commend: {}, guide: {}, applications: {} };
  for (const { field, tab } of flatFields(config)) {
    payload[tab.apiSection as ApiSection][field.dbKey as string] = state.flat[field.dbKey as string];
  }
  payload.basic_info.channel_key = channelKey;
  for (const tab of config.tabs.filter((t) => !t.apiSection)) {
    payload.applications[tab.id] = Object.fromEntries(
      applicationFields(tab).map((field) => [field.key, clone(state.applications[tab.id]?.[field.key])]),
    );
  }
  return payload;
};

/** Paths whose value differs between two form states (drives the "Discard changes?" prompt). */
export const changedPaths = (original: EnvironmentFormState, current: EnvironmentFormState): string[] => {
  const changes: string[] = [];
  for (const key of new Set([...Object.keys(original.flat), ...Object.keys(current.flat)])) {
    if (JSON.stringify(original.flat[key]) !== JSON.stringify(current.flat[key])) changes.push(key);
  }
  for (const tabId of new Set([...Object.keys(original.applications), ...Object.keys(current.applications)])) {
    const before = original.applications[tabId] ?? {};
    const after = current.applications[tabId] ?? {};
    for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
      if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) changes.push(`applications.${tabId}.${key}`);
    }
  }
  return changes;
};
